import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, Marker, Polyline, InfoWindow } from '@react-google-maps/api';
import { useGoogleMaps } from '../../components/maps/GoogleMapsLoader';
import { MapPin, AlertCircle, Home } from 'lucide-react';
import Modal from '../../components/ui/Modal';
import './RouteMapModal.css';

// Punto de origen (almacén/tienda)
const ORIGEN_COORDS = { lat: 22.1456552, lng: -102.27964 };

const RouteMapModal = ({ ruta, onClose }) => {
  const { isLoaded } = useGoogleMaps();
  const [mapCenter, setMapCenter] = useState({ lat: 21.8853, lng: -102.2916 });
  const [zoom, setZoom] = useState(12);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [geocodedPedidos, setGeocodedPedidos] = useState([]);
  const [geocoding, setGeocoding] = useState(false);

  const mapContainerStyle = {
    width: '100%',
    height: '100%'
  };

  const mapStyles = [
    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  ];

  // Colores para marcadores
  const getMarkerColor = (index, total) => {
    if (index === 0) return '#10b981'; // Verde para el primero
    if (index === total - 1) return '#ef4444'; // Rojo para el último
    return '#667eea'; // Morado para los intermedios
  };

  // Geocodificar direcciones sin coordenadas
  const geocodeAddress = useCallback(async (address) => {
    if (!window.google || !address) return null;

    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ address: address + ', Aguascalientes, México' });

      if (response.results && response.results[0]) {
        const location = response.results[0].geometry.location;
        return {
          lat: location.lat(),
          lng: location.lng()
        };
      }
    } catch (error) {
      console.error('Error geocoding:', error);
    }
    return null;
  }, []);

  // Procesar pedidos y geocodificar si es necesario
  useEffect(() => {
    const processPedidos = async () => {
      if (!isLoaded || !ruta?.pedidos) return;

      setGeocoding(true);
      const processed = [];

      for (const pedido of ruta.pedidos) {
        let coords = null;

        // Si ya tiene coordenadas, usarlas
        if (pedido.latitud && pedido.longitud) {
          coords = { lat: pedido.latitud, lng: pedido.longitud };
        } else if (pedido.direccion_entrega) {
          // Intentar geocodificar
          coords = await geocodeAddress(pedido.direccion_entrega);
        }

        processed.push({
          ...pedido,
          coords
        });
      }

      setGeocodedPedidos(processed);
      setGeocoding(false);

      // Centrar mapa en las coordenadas disponibles
      const validCoords = processed.filter(p => p.coords);
      if (validCoords.length > 0) {
        // Calcular centro promedio
        const avgLat = validCoords.reduce((sum, p) => sum + p.coords.lat, 0) / validCoords.length;
        const avgLng = validCoords.reduce((sum, p) => sum + p.coords.lng, 0) / validCoords.length;
        setMapCenter({ lat: avgLat, lng: avgLng });

        // Ajustar zoom según cantidad de marcadores
        if (validCoords.length === 1) {
          setZoom(15);
        } else if (validCoords.length <= 3) {
          setZoom(13);
        } else {
          setZoom(12);
        }
      }
    };

    processPedidos();
  }, [isLoaded, ruta, geocodeAddress]);

  // Generar path para la polilínea (incluyendo origen)
  const stopsPath = geocodedPedidos
    .filter(p => p.coords)
    .map(p => p.coords);

  // Path completo: origen -> paradas
  const polylinePath = stopsPath.length > 0
    ? [ORIGEN_COORDS, ...stopsPath]
    : [];

  // Pedidos sin coordenadas
  const pedidosSinCoords = geocodedPedidos.filter(p => !p.coords);

  return (
    <Modal
      title={`Mapa de Ruta: ${ruta?.nombre || ''}`}
      onClose={onClose}
      size="xlarge"
    >
      <div className="route-map-modal">
        {!isLoaded ? (
          <div className="map-loading">
            <MapPin size={48} />
            <p>Cargando mapa...</p>
          </div>
        ) : geocoding ? (
          <div className="map-loading">
            <div className="loading-spinner"></div>
            <p>Obteniendo ubicaciones...</p>
          </div>
        ) : (
          <>
            <div className="route-map-container">
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={mapCenter}
                zoom={zoom}
                options={{
                  streetViewControl: false,
                  mapTypeControl: true,
                  fullscreenControl: true,
                  zoomControl: true,
                  styles: mapStyles
                }}
              >
                {/* Marcador de origen (almacén) */}
                <Marker
                  position={ORIGEN_COORDS}
                  title="Punto de Origen (Almacén)"
                  icon={{
                    path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
                    scale: 6,
                    fillColor: '#1f2937',
                    fillOpacity: 1,
                    strokeColor: 'white',
                    strokeWeight: 2,
                    rotation: 0
                  }}
                  zIndex={1000}
                />

                {/* Marcadores de paradas */}
                {geocodedPedidos.map((pedido, index) => {
                  if (!pedido.coords) return null;

                  const color = getMarkerColor(index, geocodedPedidos.length);

                  return (
                    <Marker
                      key={pedido.venta_id}
                      position={pedido.coords}
                      label={{
                        text: `${index + 1}`,
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                      title={`${index + 1}. ${pedido.cliente_nombre}`}
                      onClick={() => setSelectedPedido(pedido)}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 14,
                        fillColor: color,
                        fillOpacity: 1,
                        strokeColor: 'white',
                        strokeWeight: 2
                      }}
                    />
                  );
                })}

                {/* Línea conectando los puntos */}
                {polylinePath.length > 1 && (
                  <Polyline
                    path={polylinePath}
                    options={{
                      strokeColor: '#667eea',
                      strokeOpacity: 0.8,
                      strokeWeight: 3,
                      geodesic: true
                    }}
                  />
                )}

                {/* InfoWindow para pedido seleccionado */}
                {selectedPedido && selectedPedido.coords && (
                  <InfoWindow
                    position={selectedPedido.coords}
                    onCloseClick={() => setSelectedPedido(null)}
                  >
                    <div className="marker-info">
                      <div className="marker-info-header">
                        <span className="marker-orden">#{geocodedPedidos.findIndex(p => p.venta_id === selectedPedido.venta_id) + 1}</span>
                        <span className="marker-folio">Folio: {selectedPedido.folio}</span>
                      </div>
                      <div className="marker-info-cliente">{selectedPedido.cliente_nombre}</div>
                      <div className="marker-info-direccion">{selectedPedido.direccion_entrega}</div>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            </div>

            {/* Lista de paradas */}
            <div className="route-stops-list">
              <h4>Paradas ({geocodedPedidos.length})</h4>
              <div className="stops-scroll">
                {geocodedPedidos.map((pedido, index) => (
                  <div
                    key={pedido.venta_id}
                    className={`stop-item ${!pedido.coords ? 'no-coords' : ''} ${selectedPedido?.venta_id === pedido.venta_id ? 'selected' : ''}`}
                    onClick={() => pedido.coords && setSelectedPedido(pedido)}
                  >
                    <span
                      className="stop-number"
                      style={{ backgroundColor: getMarkerColor(index, geocodedPedidos.length) }}
                    >
                      {index + 1}
                    </span>
                    <div className="stop-info">
                      <span className="stop-cliente">{pedido.cliente_nombre}</span>
                      <span className="stop-direccion">{pedido.direccion_entrega || 'Sin dirección'}</span>
                    </div>
                    {!pedido.coords && (
                      <span className="no-coords-badge" title="Sin ubicación">
                        <AlertCircle size={14} />
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Aviso de pedidos sin coordenadas */}
            {pedidosSinCoords.length > 0 && (
              <div className="no-coords-warning">
                <AlertCircle size={16} />
                <span>{pedidosSinCoords.length} parada(s) sin ubicación en el mapa</span>
              </div>
            )}

            {/* Leyenda */}
            <div className="route-map-legend">
              <div className="legend-item">
                <span className="legend-marker origin">▼</span>
                <span>Origen</span>
              </div>
              <div className="legend-item">
                <span className="legend-marker" style={{ backgroundColor: '#10b981' }}>1</span>
                <span>Primera</span>
              </div>
              <div className="legend-item">
                <span className="legend-marker" style={{ backgroundColor: '#667eea' }}>N</span>
                <span>Paradas</span>
              </div>
              <div className="legend-item">
                <span className="legend-marker" style={{ backgroundColor: '#ef4444' }}>{geocodedPedidos.length}</span>
                <span>Fin</span>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default RouteMapModal;
